import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { Link, useNavigate } from "react-router-dom";
import StatusPill from "../../components/StatusPill";
import AccountsNav from "../../components/AccountsNav";
import { toast } from "sonner";
import { WarningCircle, CheckCircle } from "@phosphor-icons/react";

// M10A FE2 — Accounts landing: "Assigned Shipments". Consumes ONLY FE1 GET /accounts/shipments.
// Row shows exactly the approved fields (code / route / status / action-required). The FE1 `id` is
// navigation-only and never rendered as business metadata.
export default function AccountsHome() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);   // null = loading
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setError(false);
    api.get("/accounts/shipments")
      .then((r) => setRows(r.data.shipments || []))
      .catch((e) => { setError(true); setRows([]); toast.error(e.displayMessage || "Failed to load"); });
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6" data-testid="accounts-home">
      <header className="mb-5">
        <AccountsNav className="mb-3" />
        <div className="text-xs mono text-stone-500">ACCOUNTS · {profile?.public_staff_id || profile?.airport_code || "—"}</div>
        <h1 className="text-2xl mono font-medium">Assigned Shipments</h1>
      </header>

      {rows === null ? (
        <div className="text-sm mono text-stone-500 py-12 text-center" data-testid="accounts-loading">Loading…</div>
      ) : error ? (
        <div className="text-sm mono text-stone-500 py-12 text-center" data-testid="accounts-error">
          Could not load your shipments.{" "}
          <button className="underline" onClick={load} data-testid="accounts-retry">Retry</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm mono text-stone-500 py-12 text-center" data-testid="accounts-empty-state">
          No shipments are assigned to you right now.
        </div>
      ) : (
        <div className="space-y-3" data-testid="accounts-shipment-list">
          {rows.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/accounts/shipments/${s.id}`)}
              className="w-full text-left border border-stone-200 rounded-lg p-4 hover:border-stone-900 transition"
              data-testid={`accounts-shipment-row-${s.shipment_code}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="mono text-sm font-medium">{s.shipment_code}</div>
                  <div className="text-xs mono text-stone-500 mt-1">
                    {s.route?.source_airport} → {s.route?.destination_airport}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <StatusPill status={s.status} />
                  {s.accounts_action_required ? (
                    <span className="inline-flex items-center gap-1 text-xs mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5"
                          data-testid={`accounts-action-required-${s.shipment_code}`}>
                      <WarningCircle size={13} weight="bold" /> Action Required
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs mono text-stone-400"
                          data-testid={`accounts-no-action-${s.shipment_code}`}>
                      <CheckCircle size={13} weight="bold" /> No action required
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="mt-8 text-center">
        <Link to="/" className="text-xs mono text-stone-500 underline" data-testid="accounts-signout">Sign out / Switch</Link>
      </div>
    </div>
  );
}
